import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { Workspace, Status, Label, WorkspaceMember } from '../../contexts/WorkspaceContext'
import { resetState } from '../actions'

export interface CreateWorkspacePayload {
    name: string
    workspaceUrlId: string
    companySize?: string
}

export interface UsageStats {
    apiCalls: number
    webhookDeliveries: number
    agentTokens: number
    agentRuns: number
}

interface WorkspaceState {
    workspace: Workspace | null
    statuses: Status[]
    labels: Label[]
    members: WorkspaceMember[]
    // True once the members listener has delivered its first snapshot. Members load
    // concurrently with (and don't gate) the global loader, so the projects page can
    // render before members arrive — this flag lets it show avatar skeletons until then.
    membersInitialized: boolean
    loading: boolean
    error: string | null
    createWorkspace: {
        error: string | null
    }
    invite: {
        loading: boolean
        error: string | null
    }
    usageStats: UsageStats | null
}

const initialState: WorkspaceState = {
    workspace: null,
    statuses: [],
    labels: [],
    members: [],
    membersInitialized: false,
    loading: true,
    error: null,
    createWorkspace: {
        error: null
    },
    invite: {
        loading: false,
        error: null
    },
    usageStats: null
}

const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        // Saga triggers
        startListening: (_state, _action: PayloadAction<string>) => {},
        stopListening: () => {},
        createWorkspaceSaga: (_state, _action: PayloadAction<CreateWorkspacePayload>) => {},

        // Status CRUD saga triggers
        addStatusSaga: (_state, _action: PayloadAction<{ name: string; color: string }>) => {},
        updateStatusSaga: (_state, _action: PayloadAction<{ statusId: string; data: Partial<{ name: string; color: string; order: number }> }>) => {},
        deleteStatusSaga: (_state, _action: PayloadAction<string>) => {},

        // Label CRUD saga triggers
        // attachToTaskId: created from a task's label picker — the worker puts it on that task
        // once the doc exists, since the caller can't know the generated id.
        addLabelSaga: (_state, _action: PayloadAction<{ name: string; color?: string; attachToTaskId?: string }>) => {},
        updateLabelSaga: (_state, _action: PayloadAction<{ labelId: string; data: Partial<{ name: string; color: string; order: number }> }>) => {},
        deleteLabelSaga: (_state, _action: PayloadAction<string>) => {},

        // Workspace update saga triggers
        updateWorkspaceNameSaga: (_state, _action: PayloadAction<string>) => {},
        updateWorkspaceAvatarSaga: (_state, _action: PayloadAction<string | null>) => {},
        updateWorkspaceCompanySizeSaga: (_state, _action: PayloadAction<string>) => {},
        updateWorkspaceUrlSaga: (_state, _action: PayloadAction<{ value: string; onError: (msg: string | null) => void }>) => {},
        updateWorkspaceIdentifierSaga: (_state, _action: PayloadAction<{ value: string; onError: (msg: string | null) => void }>) => {},

        deleteWorkspaceSaga: () => {},
        getBillingUrlSaga: () => {},
        fetchUsageStatsSaga: () => {},
        initFigmaOAuthSaga: () => {},
        clearFigmaTokenSaga: () => {},
        initSlackOAuthSaga: () => {},
        disconnectSlackSaga: () => {},

        // Member management saga triggers
        inviteMemberSaga: (_state, _action: PayloadAction<{ email: string; onInvited?: (userId: string) => void; addToAllProjects?: boolean }>) => {},
        setWorkspaceAgentSaga: (_state, _action: PayloadAction<{ enabled: boolean; onError?: () => void }>) => {},
        updateMemberRoleSaga: (_state, _action: PayloadAction<{ memberId: string; role: 'owner' | 'member' | 'guest' }>) => {},
        updateMemberStatusSaga: (_state, _action: PayloadAction<{ memberId: string; status: 'active' | 'suspended' }>) => {},
        deleteInvitationSaga: (_state, _action: PayloadAction<string>) => {},

        // State mutations
        setWorkspace: (state, action: PayloadAction<Workspace | null>) => {
            state.workspace = action.payload
        },
        setWorkspaceName: (state, action: PayloadAction<string>) => {
            if (state.workspace) state.workspace.name = action.payload
        },
        setWorkspaceAvatar: (state, action: PayloadAction<string | null>) => {
            if (state.workspace) state.workspace.avatar = action.payload
        },
        setWorkspaceCompanySize: (state, action: PayloadAction<string>) => {
            if (state.workspace) state.workspace.companySize = action.payload
        },
        updateWorkspaceTimezoneSaga: (_state, _action: PayloadAction<string>) => {},
        setWorkspaceTimezone: (state, action: PayloadAction<string>) => {
            if (state.workspace) state.workspace.timezone = action.payload
        },
        setWorkspaceUrl: (state, action: PayloadAction<string>) => {
            if (state.workspace) state.workspace.workspaceUrlId = action.payload
        },
        setWorkspaceIdentifier: (state, action: PayloadAction<string>) => {
            if (state.workspace) state.workspace.workspaceTaskPrefixId = action.payload
        },
        setStatuses: (state, action: PayloadAction<Status[]>) => {
            state.statuses = action.payload
        },
        setLabels: (state, action: PayloadAction<Label[]>) => {
            state.labels = action.payload
        },
        setMembers: (state, action: PayloadAction<WorkspaceMember[]>) => {
            state.members = action.payload
            state.membersInitialized = true
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        setCreateWorkspaceError: (state, action: PayloadAction<string | null>) => {
            state.createWorkspace.error = action.payload
        },
        clearCreateWorkspaceState: (state) => {
            state.createWorkspace.error = null
        },
        setInviteLoading: (state, action: PayloadAction<boolean>) => {
            state.invite.loading = action.payload
        },
        setInviteError: (state, action: PayloadAction<string | null>) => {
            state.invite.error = action.payload
        },
        incrementTaskCounter: (state) => {
            if (state.workspace) {
                state.workspace.taskCounter += 1
            }
        },
        setUsageStats: (state, action: PayloadAction<UsageStats | null>) => {
            state.usageStats = action.payload
        },
        clearWorkspace: (state) => {
            state.workspace = null
            state.statuses = []
            state.labels = []
            state.members = []
            state.membersInitialized = false
            state.loading = true
            state.error = null
            state.usageStats = null
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetState, () => initialState)
    }
})

export const {
    startListening,
    stopListening,
    createWorkspaceSaga,
    updateWorkspaceNameSaga,
    updateWorkspaceAvatarSaga,
    updateWorkspaceCompanySizeSaga,
    updateWorkspaceUrlSaga,
    updateWorkspaceIdentifierSaga,
    deleteWorkspaceSaga,
    getBillingUrlSaga,
    fetchUsageStatsSaga,
    initFigmaOAuthSaga,
    clearFigmaTokenSaga,
    initSlackOAuthSaga,
    disconnectSlackSaga,
    addStatusSaga,
    updateStatusSaga,
    deleteStatusSaga,
    addLabelSaga,
    updateLabelSaga,
    deleteLabelSaga,
    inviteMemberSaga,
    setWorkspaceAgentSaga,
    updateMemberRoleSaga,
    updateMemberStatusSaga,
    deleteInvitationSaga,
    setWorkspace,
    setWorkspaceName,
    setWorkspaceAvatar,
    setWorkspaceCompanySize,
    updateWorkspaceTimezoneSaga,
    setWorkspaceTimezone,
    setWorkspaceUrl,
    setWorkspaceIdentifier,
    setStatuses,
    setLabels,
    setMembers,
    setLoading,
    setError,
    setCreateWorkspaceError,
    clearCreateWorkspaceState,
    setInviteLoading,
    setInviteError,
    incrementTaskCounter,
    setUsageStats,
    clearWorkspace
} = workspaceSlice.actions

export default workspaceSlice.reducer
